import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  CircleMarker,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";
import "../../doctorScedule/basicDetails/components/loactionsMapStyle.css";
import { Badge, Card, ListGroup } from "react-bootstrap";
import Snackbar from "../../../common/Snackbar";
import SearchInput from "../../../common/InlineSearch";

// Fix Leaflet marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface Location {
  fax: string;
  phone: string;
  email: string;
  locationname: string;
  locationaddress: string;
  latitude?: number | null;
  longitude?: number | null;
  status?: "active" | "inactive" | "pending";
  distance?: number;
  count?: number;
}

type MapTheme = "light" | "dark" | "satellite" | "street";

const getInitials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => (w ? w[0] : ""))
    .join("")
    .toUpperCase();

const makeIcon = (
  bg: string,
  text: string,
  status?: string,
  isActive = false,
  isHighlighted = false
) => {
  const animationClass = isActive
    ? "map-markerhighlighted"
    : isHighlighted
    ? "bounce-slide-up map-markerhighlighted"
    : "";

  return new L.DivIcon({
    html: `<div style="position:relative; display:flex; align-items:center; justify-content:center; transition: all 0.3s ease;">
      <span class="map-marker ${animationClass}" style="font-size:32px; filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.3));"></span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    className: isActive
      ? "active-marker"
      : isHighlighted
      ? "highlighted-marker"
      : "",
  });
};

const reverseGeocode = async (lat: number, lng: number) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await response.json();
    return {
      name: data.display_name.split(",")[0] || "Your location",
      address: data.display_name || "Current location",
    };
  } catch (error) {
    console.error("Reverse geocoding error:", error);
    return { name: "Your location", address: "Current location" };
  }
};

const FitBounds = React.memo(
  ({
    pts,
    padding = [50, 50],
    shouldFit = true,
  }: {
    pts: L.LatLngExpression[];
    padding?: [number, number];
    shouldFit?: boolean;
  }) => {
    const map = useMap();
    const prevPtsRef = useRef<L.LatLngExpression[]>([]);

    useEffect(() => {
      if (pts.length > 0 && map && shouldFit) {
        if (JSON.stringify(pts) !== JSON.stringify(prevPtsRef.current)) {
          try {
            const bounds = L.latLngBounds(pts);
            map.fitBounds(bounds, { padding });
            prevPtsRef.current = pts;
          } catch (e) {
            console.error("Error fitting bounds:", e);
          }
        }
      }
    }, [pts, map, padding, shouldFit]);

    return null;
  }
);

const CurrentLocationMarker = React.memo(() => {
  const map = useMap();
  const [position, setPosition] = useState<L.LatLng | null>(null);

  useEffect(() => {
    if (!map) return;

    const handleLocationFound = (e: L.LocationEvent) => {
      setPosition(e.latlng);
      map.flyTo(e.latlng, 12);
    };

    map.locate({
      setView: false,
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });

    map.on("locationfound", handleLocationFound);

    return () => {
      map.off("locationfound", handleLocationFound);
    };
  }, [map]);

  if (!position) return null;

  return (
    <CircleMarker
      center={position}
      radius={10}
      fillOpacity={0.8}
      color="#3182ce"
      fillColor="#3182ce"
    >
      <Popup>
        <h3>Your current location</h3>
      </Popup>
    </CircleMarker>
  );
});

const SafeMarker = React.memo(
  ({
    position,
    icon,
    children,
    onClick,
    isActive,
    isHighlighted,
    onHighlight,
  }: {
    position: L.LatLngExpression;
    icon: L.Icon | L.DivIcon;
    children?: React.ReactNode;
    onClick?: () => void;
    isActive?: boolean;
    isHighlighted?: boolean;
    onHighlight?: (highlighted: boolean) => void;
  }) => {
    const [isValid, setIsValid] = useState(true);
    const markerRef = useRef<any>(null);

    const handleAdd = useCallback((e: any) => {
      try {
        if (e.target) {
          e.target._leaflet_pos = e.target._latlng;
        }
        setIsValid(true);
      } catch (error) {
        console.error("Marker error:", error);
        setIsValid(false);
      }
    }, []);

    const handleMouseOver = useCallback(() => {
      if (onHighlight) onHighlight(true);
      if (markerRef.current) {
        markerRef.current.openPopup();
      }
    }, [onHighlight]);

    const handleMouseOut = useCallback(() => {
      if (onHighlight) onHighlight(false);
      if (markerRef.current) {
        markerRef.current.closePopup();
      }
    }, [onHighlight]);

    useEffect(() => {
      return () => {
        if (markerRef.current) {
          markerRef.current.off("add", handleAdd);
        }
      };
    }, [handleAdd]);

    if (!isValid || !position) return null;

    return (
      <Marker
        position={position}
        icon={icon}
        ref={markerRef}
        eventHandlers={{
          add: handleAdd,
          mouseover: handleMouseOver,
          mouseout: handleMouseOut,
          click: () => onClick && onClick(),
        }}
      >
        {children}
      </Marker>
    );
  }
);

const LocationCard = React.memo(
  ({
    location,
    onClick,
    isActive,
    onMouseEnter,
    onMouseLeave,
    isHighlighted,
  }: {
    location: Location;
    onClick: (location: Location) => void;
    isActive: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    isHighlighted?: boolean;
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <>
        <ListGroup.Item
          action
          onClick={() => onClick(location)}
          active={isActive}
          // className="d-flex justify-content-between align-items-start"
          className={`d-flex justify-content-between align-items-start ${
            isActive ? "highlight_mapNode" : ""
          }`}
          onMouseEnter={() => {
            setIsHovered(true);
            onMouseEnter?.();
          }}
          onMouseLeave={() => {
            setIsHovered(false);
            onMouseLeave?.();
          }}
          style={{
            backgroundColor: isActive
              ? undefined
              : isHighlighted
              ? "#f8f9fa"
              : undefined,
            transition: "background-color 0.2s ease",
          }}
        >
          <span
            className={`material-symbols-outlined theme-color me-2 ${
              isHovered ? "fadeInDown" : ""
            }`}
            style={{ animationDuration: "0.5s" }}
          >
            medical_services
          </span>
          <div className="ms-2 me-auto">
            <div className="fw-bold">{location.locationname}</div>
            {location.locationaddress}
          </div>
          {location.count !== undefined && (
            <span className="border associated-location-count text-primary">
              {" "}
              {location.count}
            </span>
          )}
        </ListGroup.Item>

        {isActive && (
          <ListGroup.Item>
            {location.email !== "NA" ||
            location.phone !== "NA" ||
            location.fax !== "NA" ? (
              <div>
                {location.email !== "NA" && (
                  <div className="py-2 d-flex align-items-center px-4 border-bottom">
                    <span className="material-symbols-outlined theme-color me-2">
                      mail
                    </span>
                    <span>{location.email}</span>
                  </div>
                )}
                {location.phone !== "NA" && (
                  <div className="py-2 d-flex align-items-center px-4 border-bottom">
                    <span className="material-symbols-outlined theme-color me-2">
                      call
                    </span>
                    <span>{location.phone}</span>
                  </div>
                )}
                {location.fax !== "NA" && (
                  <div className="py-2 d-flex align-items-center px-4 border-bottom">
                    <span className="material-symbols-outlined theme-color me-2">
                      print
                    </span>
                    <span>{location.fax}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-2 px-4 text-center no_rec_data newThingsNod">
                No Location Details Found
              </div>
            )}
          </ListGroup.Item>
        )}
      </>
    );
  }
);

const MapControls = React.memo(
  ({
    theme,
    onThemeChange,
    onCurrentLocation,
    onResetView,
    isLoading,
    hasSelection,
    onRefressLocation,
  }: {
    theme: MapTheme;
    onThemeChange: (theme: MapTheme) => void;
    onCurrentLocation: () => void;
    onResetView: () => void;
    isLoading: boolean;
    hasSelection: boolean;
    onRefressLocation: () => void;
  }) => {
    return (
      <div className="utility-controls">
        <button
          onClick={onRefressLocation}
          aria-label="Refresh"
          className="location-btn d-flex align-items-center"
          disabled={isLoading}
        >
          <span className="material-symbols-outlined"> refresh </span>
        </button>
        {/* <button
          onClick={onCurrentLocation}
          aria-label='Find my location'
          className='location-btn  d-flex align-items-center'
          disabled={isLoading}
        >
          <span className='material-symbols-outlined'> my_location </span>
        </button> */}

        <select
          value={theme}
          onChange={(e) => onThemeChange(e.target.value as MapTheme)}
          aria-label="Select map theme"
          className="theme-select"
          disabled={isLoading}
        >
          <option value="street">Street</option>
          <option value="satellite">Satellite</option>
        </select>
      </div>
    );
  }
);

const FullscreenControl = React.memo(
  ({
    map,
    mapContainerRef,
    onFullscreenChange,
  }: {
    map: L.Map | null;
    mapContainerRef: React.RefObject<HTMLDivElement>;
    onFullscreenChange: (isFullscreen: boolean) => void;
  }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = useCallback(() => {
      if (!mapContainerRef.current) return;

      const container = mapContainerRef.current;
      const newFullscreenState = !isFullscreen;
      setIsFullscreen(newFullscreenState);

      if (newFullscreenState) {
        container.classList.add("custom-fullscreen");
      } else {
        container.classList.remove("custom-fullscreen");
      }

      onFullscreenChange(newFullscreenState);
      setTimeout(() => {
        map?.invalidateSize();
      }, 300);
    }, [isFullscreen, map, mapContainerRef, onFullscreenChange]);

    return (
      <div className="leaflet-top leaflet-right">
        <div className="leaflet-control leaflet-bar">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              toggleFullscreen();
            }}
            style={{
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "#333",
            }}
            aria-label={
              isFullscreen ? "Exit fullscreen mode" : "Enter fullscreen mode"
            }
          >
            <span className="material-symbols-outlined">
              {isFullscreen ? "fullscreen_exit" : "fullscreen"}
            </span>
          </button>
        </div>
      </div>
    );
  }
);

const MapOnAppointment: React.FC<{ locations: Location[] }> = ({
  locations,
}) => {
  const [theme, setTheme] = useState<MapTheme>("street");
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeLocation, setActiveLocation] = useState<Location | null>(null);
  const [hasUserSelectedLocation, setHasUserSelectedLocation] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hoveredLocation, setHoveredLocation] = useState<Location | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<L.LatLng | null>(
    null
  );
  const [selectedMarker, setSelectedMarker] = useState<Location | null>(null);

  // Process locations on mount and when locations prop changes
  useEffect(() => {
    const processLocations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const processedLocations = locations.map((loc) => {
          const lat =
            typeof loc.latitude === "number"
              ? loc.latitude
              : loc.latitude && !isNaN(parseFloat(String(loc.latitude)))
              ? parseFloat(String(loc.latitude))
              : NaN;

          const lng =
            typeof loc.longitude === "number"
              ? loc.longitude
              : loc.longitude && !isNaN(parseFloat(String(loc.longitude)))
              ? parseFloat(String(loc.longitude))
              : NaN;

          return {
            ...loc,
            latitude: isNaN(lat) ? null : lat,
            longitude: isNaN(lng) ? null : lng,
          };
        });

        const validLocations = processedLocations.filter(
          (loc) => loc.latitude !== null && loc.longitude !== null
        );
        setFilteredLocations(validLocations);
      } catch (err) {
        console.error("Error processing locations:", err);
        setError("Failed to process locations");
      } finally {
        setIsLoading(false);
      }
    };

    if (locations?.length > 0) {
      processLocations();
    } else {
      setFilteredLocations([]);
    }
  }, [locations]);

  const handleCurrentLocation = useCallback(async () => {
    if (currentLocation) {
      mapRef.current?.flyTo([currentLocation.lat, currentLocation.lng], 12);
      return;
    }

    setIsGeolocating(true);

    try {
      // Try browser geolocation first (works on HTTPS/localhost)
      if (navigator.geolocation) {
        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
            });
          }
        );
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
        mapRef.current?.flyTo([latitude, longitude], 12);
      }
      // Fallback to IP geolocation if browser geolocation fails/HTTP
      else {
        const ipGeoResponse = await fetch("https://ipapi.co/json/");
        const { latitude, longitude } = await ipGeoResponse.json();
        if (latitude && longitude) {
          setCurrentLocation({ lat: latitude, lng: longitude });
          mapRef.current?.flyTo([latitude, longitude], 10);
        } else {
          throw new Error("Could not determine your location");
        }
      }
      setError(null);
      setSnackbarOpen(false);
    } catch (error) {
      console.error("Geolocation error:", error);
      setSnackbarMessage(
        error instanceof GeolocationPositionError
          ? error.code === error.PERMISSION_DENIED
            ? "Location access is required. Please enable location permissions in your browser settings."
            : "Failed to get your location. Please try again."
          : "An unexpected error occurred."
      );
      setSnackbarOpen(true);

      if (mapRef.current && filteredLocations.length > 0) {
        const bounds = L.latLngBounds(
          filteredLocations.map((loc) => [loc.latitude!, loc.longitude!])
        );
        mapRef.current.flyToBounds(bounds, { padding: [20, 20] });
      }
    } finally {
      setIsGeolocating(false);
    }
  }, [currentLocation, filteredLocations]);

  const handleMapReady = useCallback(() => {
    setMapReady(true);
  }, []);

  const handleLocationClick = useCallback((location: Location) => {
    if (!location.latitude || !location.longitude) return;

    const position = L.latLng(location.latitude, location.longitude);
    setSelectedPosition(position);
    setSelectedMarker(location);
    setActiveLocation(location);
    setHasUserSelectedLocation(true);
    mapRef.current?.flyTo(position, 15);
  }, []);

  const handleResetView = useCallback(() => {
    setHasUserSelectedLocation(false);
    setActiveLocation(null);
    setSelectedPosition(null);
    setSelectedMarker(null);

    if (mapRef.current && filteredLocations.length > 0) {
      const bounds = L.latLngBounds(
        filteredLocations.map((loc) => [loc.latitude!, loc.longitude!])
      );
      mapRef.current.flyToBounds(bounds, { padding: [20, 20] });
    }
  }, [filteredLocations]);

  const handleLocationHover = useCallback((location: Location | null) => {
    setHoveredLocation((prev) =>
      prev?.locationname === location?.locationname ? prev : location
    );
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const filteredSearchLocations = useMemo(() => {
    if (!searchQuery.trim()) return filteredLocations;

    const searchLower = searchQuery.toLowerCase();
    return filteredLocations.filter((location) =>
      ["locationname", "locationaddress", "email", "phone", "fax"].some((key) =>
        String(location[key as keyof Location])
          .toLowerCase()
          .includes(searchLower)
      )
    );
  }, [searchQuery, filteredLocations]);

  const renderMarkers = useMemo(() => {
    if (!mapReady) return null;

    return filteredLocations.map((loc) => {
      if (!loc.latitude || !loc.longitude) return null;

      const isHighlighted =
        selectedMarker?.locationname === loc.locationname ||
        hoveredLocation?.locationname === loc.locationname;

      return (
        <SafeMarker
          key={`${loc.locationname}-${loc.locationaddress}`}
          position={[loc.latitude, loc.longitude]}
          icon={makeIcon(
            activeLocation?.locationname === loc.locationname
              ? "#ff2222"
              : isHighlighted
              ? "#ff9900"
              : "#3182ce",
            loc.locationname,
            loc.status,
            activeLocation?.locationname === loc.locationname,
            isHighlighted
          )}
          onClick={() => handleLocationClick(loc)}
          isActive={activeLocation?.locationname === loc.locationname}
          isHighlighted={isHighlighted}
          onHighlight={(highlighted) =>
            handleLocationHover(highlighted ? loc : null)
          }
        >
          <Popup minWidth={260} maxWidth={260}>
            <div className="ms-1 me-auto">
              <div className="fw-bold mb-1">{loc.locationname}</div>
              {loc.locationaddress}
            </div>
          </Popup>
        </SafeMarker>
      );
    });
  }, [
    mapReady,
    filteredLocations,
    selectedMarker,
    hoveredLocation,
    activeLocation,
    handleLocationClick,
    handleLocationHover,
  ]);

  const getTileLayer = useCallback(() => {
    switch (theme) {
      case "satellite":
        return (
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          />
        );
      case "street":
      default:
        return (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
        );
    }
  }, [theme]);

  if (error) {
    return (
      <div className="map-error">
        <p>{error}</p>
        <button onClick={() => setError(null)}>Dismiss</button>
      </div>
    );
  }

  return (
    <div className="map-container" ref={mapContainerRef}>
      {isLoading && (
        <div className="map-loader">
          <div className="spinner"></div>
          <p>Loading map data...</p>
        </div>
      )}
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity="warning"
      />
      <div className="map-layout setHeightFullInner ">
        <div className="map-wrapper">
          <MapControls
            theme={theme}
            onThemeChange={setTheme}
            onCurrentLocation={handleCurrentLocation}
            onResetView={handleResetView}
            isLoading={isGeolocating || isLoading}
            hasSelection={hasUserSelectedLocation}
            onRefressLocation={handleResetView}
          />

          <MapContainer
            center={currentLocation || [37.0902, -95.7129]}
            zoom={currentLocation ? 10 : 5}
            minZoom={2}
            maxZoom={18}
            worldCopyJump={true}
            maxBounds={[
              [-85, -180],
              [85, 180],
            ]}
            maxBoundsViscosity={0.75}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
            whenReady={handleMapReady}
            ref={mapRef}
          >
            {mapReady && (
              <>
                {getTileLayer()}
                {mapRef.current && (
                  <FullscreenControl
                    map={mapRef.current}
                    mapContainerRef={mapContainerRef}
                    onFullscreenChange={setIsFullscreen}
                  />
                )}
                <FitBounds
                  pts={filteredLocations.map((loc) => [
                    loc.latitude!,
                    loc.longitude!,
                  ])}
                  padding={[20, 20]}
                  shouldFit={!hasUserSelectedLocation}
                />
                {currentLocation && <CurrentLocationMarker />}
                {renderMarkers}
              </>
            )}
          </MapContainer>
        </div>
        <div className="locations-sidebar">
          <Card style={{ width: "100%" }}>
            {isFullscreen && (
              <Card.Header>
                <h5
                  className="mx-1 me-auto position-relative mb-0"
                  style={{ lineHeight: "20px" }}
                >
                  Associated Locations
                </h5>
              </Card.Header>
            )}
            <div style={{ width: "100%" }} className="gridsearch">
              <div className="searchWrapper">
                <div className="Searchlistdata">
                  <SearchInput
                    data={filteredLocations}
                    searchKeys={[
                      "locationname",
                      "locationaddress",
                      "email",
                      "phone",
                      "fax",
                    ]}
                    onSearchResult={(results) => {}}
                    placeholder="Search"
                    onSearchQueryChange={handleSearch}
                    onInputChange={handleSearch}
                  />
                </div>
              </div>
            </div>
            <ListGroup variant="flush">
              {filteredSearchLocations.length > 0 ? (
                filteredSearchLocations.map((location) => (
                  <LocationCard
                    key={`${location.locationname}-${location.locationaddress}`}
                    location={location}
                    onClick={handleLocationClick}
                    isActive={
                      activeLocation?.locationname === location.locationname
                    }
                    isHighlighted={
                      selectedMarker?.locationname === location.locationname ||
                      hoveredLocation?.locationname === location.locationname
                    }
                    onMouseEnter={() => handleLocationHover(location)}
                    onMouseLeave={() => handleLocationHover(null)}
                  />
                ))
              ) : (
                <ListGroup.Item className="text-center py-4 no_rec_data newThingsNod">
                  No Associated Locations Found
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MapOnAppointment;
